// Gamepads into navigation intents, for menus: the W3C standard mapping's d-pad (12 to 15) and left stick move,
// button 0 (south) confirms, button 1 (east) and 9 (start) go back. A held direction repeats after a delay, as
// console menus do. Pure: the caller polls `navigator.getGamepads()` and passes what it read with the time.
import type { NavIntent } from "./nav";

/** What the reader needs of a gamepad (the DOM Gamepad fits). */
export interface PadLike {
  buttons: readonly { pressed: boolean }[];
  axes: readonly number[];
}

/** Repeat timing and the stick's dead zone. */
export interface PadNavOptions {
  /** ms before a held direction repeats (default 400). */
  repeatDelayMs?: number;
  /** ms between repeats (default 120). */
  repeatMs?: number;
  /** Stick deflection that counts as a direction (default 0.5). */
  dead?: number;
}

const DIRS = ["up", "down", "left", "right"] as const;
const BUTTON_DIR: Record<number, (typeof DIRS)[number]> = { 12: "up", 13: "down", 14: "left", 15: "right" };
const BUTTON_ACT: Record<number, NavIntent> = { 0: "confirm", 1: "back", 9: "back" };

/** A reader that turns successive pad states into intents (presses, plus repeats while a direction is held). */
export function createPadNav(o: PadNavOptions = {}): (pads: readonly (PadLike | null | undefined)[], nowMs: number) => NavIntent[] {
  const delay = o.repeatDelayMs ?? 400;
  const every = o.repeatMs ?? 120;
  const dead = o.dead ?? 0.5;
  const held = new Map<string, number>(); // intent → next repeat time
  return (pads, now) => {
    const down = new Set<NavIntent>();
    for (const p of pads) {
      if (!p) continue;
      p.buttons.forEach((b, i) => {
        if (!b?.pressed) return;
        const d = BUTTON_DIR[i] ?? BUTTON_ACT[i];
        if (d) down.add(d);
      });
      const [x = 0, y = 0] = p.axes;
      if (y < -dead) down.add("up");
      if (y > dead) down.add("down");
      if (x < -dead) down.add("left");
      if (x > dead) down.add("right");
    }
    const out: NavIntent[] = [];
    for (const k of [...held.keys()]) if (!down.has(k as NavIntent)) held.delete(k);
    for (const d of down) {
      const next = held.get(d);
      if (next === undefined) {
        out.push(d);
        // confirm and back never repeat
        held.set(d, (DIRS as readonly string[]).includes(d) ? now + delay : Infinity);
      } else if (now >= next) {
        out.push(d);
        held.set(d, now + every);
      }
    }
    return out;
  };
}
